import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { Plus, ChevronRight, ChevronDown, Calendar as CalendarIcon, User, Link2, Trash2, Edit, CheckCircle2, Circle, Clock, Lock, ChevronUp, ArrowUpDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Render subtask list iteratively instead of recursively
function renderSubtaskList(items, props, depth = 0, parentIndex = 0) {
  const result = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const isFirst = i === 0;
    const isLast = i === items.length - 1;
    result.push(
      <SubTaskRow 
        key={item.id} 
        subtask={item} 
        allSubtasks={props.allSubtasks} 
        depth={depth} 
        isFirst={isFirst && depth === 0}
        isLast={isLast && depth === 0}
        {...props} 
      />
    );
    if (item.children && item.children.length > 0) {
      result.push(...renderSubtaskList(item.children, props, depth + 1, i));
    }
  }
  return result;
}

function SubTaskRow({ subtask, allSubtasks, depth, taskId, token, users, onRefresh, onAddChild, onMoveUp, onMoveDown, isFirst, isLast, sortMode }) {
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ 
    name: subtask.name, 
    description: subtask.description || '',
    due_date: subtask.due_date ? parseISO(subtask.due_date) : null,
    assigned_to: subtask.assigned_to || '',
    dependencies: subtask.dependencies || []
  });

  // Reset form when subtask changes or dialog opens
  const openEditDialog = () => {
    setEditForm({
      name: subtask.name,
      description: subtask.description || '',
      due_date: subtask.due_date ? parseISO(subtask.due_date) : null,
      assigned_to: subtask.assigned_to || '',
      dependencies: subtask.dependencies || []
    });
    setEditing(true);
  };

  const isBlocked = subtask.dependency_status === 'blocked';
  const isCompleted = subtask.status === 'completed';
  const statusColors = {
    new: 'bg-purple-500/20 text-purple-600',
    'in progress': 'bg-amber-500/20 text-amber-600',
    completed: 'bg-emerald-500/20 text-emerald-600'
  };

  const toggleStatus = async () => {
    if (isBlocked) return toast.error('Blocked by dependencies');
    const newStatus = subtask.status === 'completed' ? 'new' : subtask.status === 'new' ? 'in progress' : 'completed';
    try {
      await fetch(`${API}/tasks/${taskId}/subtasks/${subtask.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      onRefresh();
    } catch (e) { toast.error('Failed'); }
  };

  const saveEdit = async () => {
    try {
      await fetch(`${API}/tasks/${taskId}/subtasks/${subtask.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: editForm.name,
          description: editForm.description,
          due_date: editForm.due_date ? format(editForm.due_date, 'yyyy-MM-dd') : null,
          assigned_to: editForm.assigned_to || null,
          dependencies: editForm.dependencies
        })
      });
      setEditing(false);
      onRefresh();
      toast.success('Sub-task updated');
    } catch (e) { toast.error('Failed to save'); }
  };

  const deleteSubtask = async () => {
    if (!window.confirm('Delete?')) return;
    try {
      await fetch(`${API}/tasks/${taskId}/subtasks/${subtask.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh();
    } catch (e) { toast.error('Failed'); }
  };

  const toggleDependency = (depId) => {
    setEditForm(prev => ({
      ...prev,
      dependencies: prev.dependencies.includes(depId)
        ? prev.dependencies.filter(id => id !== depId)
        : [...prev.dependencies, depId]
    }));
  };

  // Get available subtasks for dependencies (exclude self and children)
  const getAvailableDependencies = () => {
    return allSubtasks.filter(s => 
      s.id !== subtask.id && // Can't depend on self
      s.parent_subtask_id !== subtask.id // Can't depend on own children
    );
  };

  // Get dependency names for display
  const getDependencyNames = () => {
    if (!subtask.dependencies || subtask.dependencies.length === 0) return null;
    return subtask.dependencies.map(depId => {
      const dep = allSubtasks.find(s => s.id === depId);
      return dep ? dep.name : 'Unknown';
    });
  };

  const dependencyNames = getDependencyNames();

  return (
    <React.Fragment>
      <div 
        className={`group flex items-start gap-2 p-3 rounded-lg border mb-2 hover:bg-muted/50 ${isBlocked ? 'border-red-500/30' : ''}`}
        style={{ marginLeft: depth * 24 }}
      >
        {/* Reorder arrows - only show in manual sort mode */}
        {sortMode === 'manual' && depth === 0 && (
          <div className="flex flex-col gap-0.5 mr-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-5 w-5" 
              onClick={() => onMoveUp(subtask.id)}
              disabled={isFirst}
              title="Move up"
            >
              <ChevronUp className={`w-3 h-3 ${isFirst ? 'text-muted-foreground/30' : ''}`} />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-5 w-5" 
              onClick={() => onMoveDown(subtask.id)}
              disabled={isLast}
              title="Move down"
            >
              <ChevronDown className={`w-3 h-3 ${isLast ? 'text-muted-foreground/30' : ''}`} />
            </Button>
          </div>
        )}
        
        <button onClick={toggleStatus} disabled={isBlocked} className="mt-0.5 flex-shrink-0">
          {isCompleted ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> :
           isBlocked ? <Lock className="w-5 h-5 text-red-500" /> :
           subtask.status === 'in progress' ? <Clock className="w-5 h-5 text-amber-500" /> :
           <Circle className="w-5 h-5 text-muted-foreground" />}
        </button>

        <div className="flex-1 min-w-0">
          <p className={`font-medium ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>{subtask.name}</p>
          {subtask.description && <p className="text-sm text-muted-foreground">{subtask.description}</p>}
          <div className="flex flex-wrap gap-2 mt-2 text-xs">
            <Badge variant="outline" className={statusColors[subtask.status]}>{subtask.status}</Badge>
            {subtask.assigned_to_name && <span className="flex items-center gap-1 text-muted-foreground"><User className="w-3 h-3" />{subtask.assigned_to_name}</span>}
            {subtask.due_date && <span className="flex items-center gap-1 text-muted-foreground"><CalendarIcon className="w-3 h-3" />{format(parseISO(subtask.due_date), 'MMM d')}</span>}
            {dependencyNames && dependencyNames.length > 0 && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <Link2 className="w-3 h-3" />
                Depends on: {dependencyNames.join(', ')}
              </span>
            )}
            {isBlocked && <Badge variant="destructive" className="text-xs"><Lock className="w-3 h-3 mr-1" />Blocked</Badge>}
          </div>
        </div>

        <div className="flex gap-1 opacity-0 group-hover:opacity-100 flex-shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onAddChild(subtask.id)} title="Add nested"><Plus className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={openEditDialog}><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={deleteSubtask}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle>Edit Sub-Task</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} rows={2} />
            </div>
            
            {/* Due Date and Assigned To */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {editForm.due_date ? format(editForm.due_date, 'PPP') : 'Select'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar 
                      mode="single" 
                      selected={editForm.due_date} 
                      onSelect={d => setEditForm({...editForm, due_date: d})} 
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Assign To</Label>
                <Select 
                  value={editForm.assigned_to || "unassigned"} 
                  onValueChange={v => setEditForm({...editForm, assigned_to: v === "unassigned" ? "" : v})}
                >
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Dependencies Section */}
            {getAvailableDependencies().length > 0 && (
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Link2 className="w-4 h-4" />
                  Dependencies (must complete before this)
                </Label>
                <ScrollArea className="h-32 border rounded-lg p-2">
                  {getAvailableDependencies().map(s => (
                    <div 
                      key={s.id} 
                      className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer" 
                      onClick={() => toggleDependency(s.id)}
                    >
                      <Checkbox checked={editForm.dependencies.includes(s.id)} />
                      <span className="text-sm flex-1">{s.name}</span>
                      <Badge variant="outline" className="text-xs">{s.status}</Badge>
                    </div>
                  ))}
                </ScrollArea>
                {editForm.dependencies.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {editForm.dependencies.length} dependenc{editForm.dependencies.length === 1 ? 'y' : 'ies'} selected
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            <Button onClick={saveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}

export default function SubTasks({ taskId, externalControls = false, onControlsReady = null }) {
  const { token } = useAuth();
  const [subtasks, setSubtasks] = useState([]);
  const [flatList, setFlatList] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [parentId, setParentId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', due_date: null, assigned_to: '', dependencies: [] });
  const [sortMode, setSortMode] = useState('manual'); // 'manual' or 'due_date'

  const fetchData = useCallback(async () => {
    try {
      const [treeRes, flatRes, usersRes] = await Promise.all([
        fetch(`${API}/tasks/${taskId}/subtasks`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/tasks/${taskId}/subtasks?flat=true`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (treeRes.ok) setSubtasks(await treeRes.json());
      if (flatRes.ok) setFlatList(await flatRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [taskId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sort subtasks based on mode
  const getSortedSubtasks = () => {
    if (sortMode === 'due_date') {
      // Sort by due date (items without due date go to end)
      return [...subtasks].sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      });
    }
    return subtasks; // Manual order (default from backend)
  };

  // Move subtask up in order
  const handleMoveUp = async (subtaskId) => {
    const rootSubtasks = subtasks.filter(s => !s.parent_subtask_id);
    const currentIndex = rootSubtasks.findIndex(s => s.id === subtaskId);
    if (currentIndex <= 0) return;
    
    // Swap with previous item
    const newOrder = rootSubtasks.map(s => s.id);
    [newOrder[currentIndex], newOrder[currentIndex - 1]] = [newOrder[currentIndex - 1], newOrder[currentIndex]];
    
    try {
      await fetch(`${API}/tasks/${taskId}/subtasks/reorder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subtask_ids: newOrder })
      });
      fetchData();
    } catch (e) { toast.error('Failed to reorder'); }
  };

  // Move subtask down in order
  const handleMoveDown = async (subtaskId) => {
    const rootSubtasks = subtasks.filter(s => !s.parent_subtask_id);
    const currentIndex = rootSubtasks.findIndex(s => s.id === subtaskId);
    if (currentIndex < 0 || currentIndex >= rootSubtasks.length - 1) return;
    
    // Swap with next item
    const newOrder = rootSubtasks.map(s => s.id);
    [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
    
    try {
      await fetch(`${API}/tasks/${taskId}/subtasks/reorder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subtask_ids: newOrder })
      });
      fetchData();
    } catch (e) { toast.error('Failed to reorder'); }
  };

  const openModal = useCallback((pId = null) => {
    setParentId(pId);
    setForm({ name: '', description: '', due_date: null, assigned_to: '', dependencies: [] });
    setShowModal(true);
  }, []);

  const countAll = (items) => items.reduce((c, i) => c + 1 + (i.children ? countAll(i.children) : 0), 0);
  const total = countAll(subtasks);
  const completed = flatList.filter(s => s.status === 'completed').length;
  const sortedSubtasks = getSortedSubtasks();

  // Expose controls to parent when externalControls is true
  useEffect(() => {
    if (externalControls && onControlsReady) {
      onControlsReady({
        sortMode,
        setSortMode,
        openAddModal: () => openModal(null),
        total,
        completed
      });
    }
  }, [externalControls, onControlsReady, sortMode, total, completed, openModal]);

  const createSubtask = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    try {
      const res = await fetch(`${API}/tasks/${taskId}/subtasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: form.name,
          description: form.description,
          due_date: form.due_date ? format(form.due_date, 'yyyy-MM-dd') : null,
          assigned_to: form.assigned_to || null,
          parent_subtask_id: parentId,
          dependencies: form.dependencies
        })
      });
      if (res.ok) {
        toast.success('Created');
        setShowModal(false);
        setForm({ name: '', description: '', due_date: null, assigned_to: '', dependencies: [] });
        setParentId(null);
        fetchData();
      }
    } catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="py-4 text-muted-foreground">Loading sub-tasks...</div>;

  const rowProps = { 
    taskId, 
    token, 
    users, 
    allSubtasks: flatList, 
    onRefresh: fetchData, 
    onAddChild: openModal,
    onMoveUp: handleMoveUp,
    onMoveDown: handleMoveDown,
    sortMode
  };

  return (
    <div className="space-y-4">
      {/* Only show header if not using external controls */}
      {!externalControls && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            <h3 className="font-heading text-lg font-semibold">Sub-Tasks</h3>
            {total > 0 && <Badge variant="secondary">{completed}/{total}</Badge>}
          </div>
          <div className="flex items-center gap-2">
            {/* Sort Mode Dropdown */}
            <Select value={sortMode} onValueChange={setSortMode}>
              <SelectTrigger className="w-[140px] h-8">
                <ArrowUpDown className="w-3 h-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual Order</SelectItem>
                <SelectItem value="due_date">Due Date</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => openModal(null)} data-testid="add-subtask-btn">
              <Plus className="w-4 h-4 mr-1" />Add Sub-Task
            </Button>
          </div>
        </div>
      )}

      {total > 0 && (
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(completed/total)*100}%` }} />
        </div>
      )}

      {subtasks.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground mb-2">No sub-tasks yet</p>
          <Button variant="outline" size="sm" onClick={() => openModal(null)}>
            <Plus className="w-4 h-4 mr-1" />Create First Sub-Task
          </Button>
        </div>
      ) : (
        <div>{renderSubtaskList(sortedSubtasks, rowProps)}</div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle>{parentId ? 'Add Nested Sub-Task' : 'Create Sub-Task'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="subtask-name-input" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.due_date ? format(form.due_date, 'PPP') : 'Select'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={form.due_date} onSelect={d => setForm({...form, due_date: d})} /></PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Assign To</Label>
                <Select value={form.assigned_to || "unassigned"} onValueChange={v => setForm({...form, assigned_to: v === "unassigned" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {flatList.length > 0 && (
              <div>
                <Label className="flex items-center gap-2"><Link2 className="w-4 h-4" />Dependencies</Label>
                <ScrollArea className="h-28 border rounded-lg p-2 mt-2">
                  {flatList.filter(s => s.id !== parentId).map(s => (
                    <div 
                      key={s.id} 
                      className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer" 
                      onClick={() => setForm(f => ({
                        ...f, 
                        dependencies: f.dependencies.includes(s.id) 
                          ? f.dependencies.filter(id => id !== s.id) 
                          : [...f.dependencies, s.id]
                      }))}
                    >
                      <Checkbox checked={form.dependencies.includes(s.id)} />
                      <span className="text-sm flex-1">{s.name}</span>
                      <Badge variant="outline" className="text-xs">{s.status}</Badge>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={createSubtask} data-testid="create-subtask-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
